import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard,
  Briefcase,
  Users,
  Bot,
  Brain,
  BarChart,
  Settings,
  FileText,
  CalendarCheck,
  Plus,
  Sparkles,
  Activity,
  Bell,
  Sun,
  Moon,
  CheckCircle2,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface GlobalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ProjectRow {
  id: string;
  name: string;
  status: string | null;
}

interface TaskRow {
  id: string;
  name: string;
  status: string | null;
  project_id: string;
  end_date: string | null;
}

export const GlobalSearchDialog: React.FC<GlobalSearchDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const themeCtx = useTheme();
  const [query, setQuery] = useState('');

  // Fetch projects (cached 5 min)
  const { data: projects = [] } = useQuery<ProjectRow[]>({
    queryKey: ['command-palette-projects', user?.id],
    enabled: !!user && open,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, status')
        .order('updated_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as ProjectRow[];
    },
  });

  // Fetch tasks (cached 2 min, smaller set)
  const { data: tasks = [] } = useQuery<TaskRow[]>({
    queryKey: ['command-palette-tasks', user?.id],
    enabled: !!user && open,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_tasks')
        .select('id, name, status, project_id, end_date')
        .order('updated_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as unknown as TaskRow[];
    },
  });

  // Reset query when dialog closes
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const go = (path: string) => {
    navigate(path);
    onOpenChange(false);
  };

  const filteredProjects = useMemo(() => {
    if (!query.trim()) return projects.slice(0, 6);
    const q = query.toLowerCase();
    return projects.filter((p) => p.name?.toLowerCase().includes(q)).slice(0, 8);
  }, [projects, query]);

  const filteredTasks = useMemo(() => {
    if (!query.trim()) return tasks.slice(0, 5);
    const q = query.toLowerCase();
    return tasks.filter((t) => t.name?.toLowerCase().includes(q)).slice(0, 8);
  }, [tasks, query]);

  const taskStatusIcon = (status: string | null) => {
    switch (status) {
      case 'Completado':
        return <CheckCircle2 className="h-3.5 w-3.5 text-success" />;
      case 'En progreso':
        return <Clock className="h-3.5 w-3.5 text-info" />;
      case 'Bloqueado':
        return <AlertCircle className="h-3.5 w-3.5 text-danger" />;
      default:
        return <Clock className="h-3.5 w-3.5 text-muted-foreground" />;
    }
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Buscar proyectos, tareas, ir a..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[420px]">
        <CommandEmpty>No se encontraron resultados.</CommandEmpty>

        {/* Quick actions */}
        <CommandGroup heading="Acciones rápidas">
          <CommandItem onSelect={() => go('/my-day')} value="mi dia hoy tareas">
            <Sparkles className="mr-2 h-4 w-4 text-primary" />
            <span>Ir a Mi Día</span>
            <CommandShortcut>nuevo</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => go('/projects')} value="nuevo proyecto crear">
            <Plus className="mr-2 h-4 w-4" />
            <span>Crear nuevo proyecto</span>
          </CommandItem>
          <CommandItem onSelect={() => go('/virtual-agent')} value="valen ai asistente">
            <Bot className="mr-2 h-4 w-4" />
            <span>Hablar con VALEN</span>
          </CommandItem>
          <CommandItem
            onSelect={() =>
              themeCtx?.updateColorScheme?.(
                themeCtx.colorScheme === 'dark' ? 'light' : 'dark'
              )
            }
            value="tema oscuro claro modo"
          >
            <Sun className="mr-2 h-4 w-4 dark:hidden" />
            <Moon className="mr-2 h-4 w-4 hidden dark:block" />
            <span>Cambiar tema (claro / oscuro)</span>
          </CommandItem>
        </CommandGroup>

        {filteredProjects.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Proyectos">
              {filteredProjects.map((p) => (
                <CommandItem
                  key={p.id}
                  value={`proyecto ${p.name}`}
                  onSelect={() => go(`/project-management/${p.id}`)}
                >
                  <Briefcase className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span className="truncate">{p.name}</span>
                  {p.status && (
                    <span className="ml-auto text-xs text-muted-foreground">
                      {p.status}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {filteredTasks.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Tareas">
              {filteredTasks.map((t) => (
                <CommandItem
                  key={t.id}
                  value={`tarea ${t.name}`}
                  onSelect={() => go(`/project-management/${t.project_id}`)}
                >
                  {taskStatusIcon(t.status)}
                  <span className="ml-2 truncate">{t.name}</span>
                  {t.end_date && (
                    <span className="ml-auto text-xs text-muted-foreground">
                      {new Date(t.end_date).toLocaleDateString()}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        <CommandSeparator />
        <CommandGroup heading="Navegación">
          <CommandItem onSelect={() => go('/dashboard')} value="dashboard inicio">
            <LayoutDashboard className="mr-2 h-4 w-4" />
            <span>Dashboard</span>
          </CommandItem>
          <CommandItem onSelect={() => go('/projects')} value="proyectos lista">
            <Briefcase className="mr-2 h-4 w-4" />
            <span>Proyectos</span>
          </CommandItem>
          <CommandItem onSelect={() => go('/task-track')} value="tareas track gestion">
            <CalendarCheck className="mr-2 h-4 w-4" />
            <span>Gestión de tareas</span>
          </CommandItem>
          <CommandItem onSelect={() => go('/team')} value="equipo miembros">
            <Users className="mr-2 h-4 w-4" />
            <span>Equipo</span>
          </CommandItem>
          <CommandItem onSelect={() => go('/ai-predictive')} value="ia predictiva">
            <Brain className="mr-2 h-4 w-4" />
            <span>IA Predictiva</span>
          </CommandItem>
          <CommandItem onSelect={() => go('/analytics')} value="analytics analisis">
            <BarChart className="mr-2 h-4 w-4" />
            <span>Analytics</span>
          </CommandItem>
          <CommandItem onSelect={() => go('/executive-report')} value="reporte ejecutivo">
            <FileText className="mr-2 h-4 w-4" />
            <span>Reporte Ejecutivo</span>
          </CommandItem>
          <CommandItem onSelect={() => go('/alerts')} value="alertas notificaciones">
            <Bell className="mr-2 h-4 w-4" />
            <span>Alertas</span>
          </CommandItem>
          <CommandItem
            onSelect={() => go('/task-track/activity-summary')}
            value="actividad resumen"
          >
            <Activity className="mr-2 h-4 w-4" />
            <span>Resumen de Actividad</span>
          </CommandItem>
          <CommandItem onSelect={() => go('/settings')} value="configuracion ajustes">
            <Settings className="mr-2 h-4 w-4" />
            <span>Configuración</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
